/* ============================================================
   ORION SUBSCRIPTION BUILDER

   Lets someone build the subscription from anywhere on the site,
   not only from a product page. Two renderings share this class:
   an inline one on the Subscriptions page and a site-wide dialog
   opened from a nudge.

   Price and cadence come from the products' real selling plans,
   rendered into the page by Liquid. Nothing here calls the StayAI
   API — that token is a store-wide admin credential and would
   expose every subscriber if it were shipped to a browser.
   ============================================================ */
(() => {
  if (customElements.get('orion-sub-builder')) return;

class OrionSubBuilder extends HTMLElement {
  connectedCallback() {
    const raw = this.querySelector('[data-o-sub-data]');
    if (!raw) return;

    try {
      this.data = JSON.parse(raw.textContent);
    } catch (e) {
      return;
    }
    if (!this.data.flavours || !this.data.flavours.length) return;

    this.strings = window.orionSubStrings || {};
    this.overlay = this.dataset.display === 'overlay';

    this.els = {
      tiles: this.querySelector('[data-o-sub-tiles]'),
      flavourName: this.querySelector('[data-o-sub-flavour-name]'),
      plans: this.querySelector('[data-o-sub-plans]'),
      cadenceStep: this.querySelector('[data-o-sub-cadence-step]'),
      cadence: this.querySelector('[data-o-sub-cadence]'),
      cta: this.querySelector('[data-o-sub-cta]'),
      error: this.querySelector('[data-o-sub-error]'),
      legal: this.querySelector('[data-o-sub-legal]'),
      dialog: this.querySelector('[data-o-sub-dialog]'),
      nudge: this.querySelector('[data-o-sub-open]'),
    };

    const firstAvailable = this.data.flavours.findIndex((f) => f.available);
    this.state = {
      flavour: firstAvailable === -1 ? 0 : firstAvailable,
      size: 0,
      plan: 'sub',
      cadence: 0,
    };

    this.cadences = (this.dataset.cadences || '30,45,60')
      .split(',')
      .map((n) => parseInt(n, 10))
      .filter(Boolean);

    /* Ladder mode: when the products carry "Box of N · every C days" plans
       (the DPP model), the builder grows a box-size step, quantities become
       cases (N / smallest N), and the plan is the exact size+cadence match.
       Without ladder plans it behaves as before (one unit, flavour+cadence). */
    this.ladder = this.data.flavours.map((f) => this.parseLadder(f)).find(Boolean) || null;
    if (this.ladder) {
      this.cadences = this.ladder.cadences;
      const popular = this.ladder.sizes.indexOf(45);
      this.state.size = popular === -1 ? 0 : popular;
      const sizeStep = this.querySelector('[data-o-sub-size-step]');
      if (sizeStep) sizeStep.hidden = false;
      const mixHint = this.querySelector('[data-o-sub-mix-hint]');
      if (mixHint) mixHint.hidden = false;
      this.els.sizes = this.querySelector('[data-o-sub-sizes]');
      if (this.els.sizes) this.renderSizes();
    }

    this.renderTiles();
    this.renderCadence();
    this.paint();
    this.wire();
  }

  /* "Box of 45 · every 30 days" → sizes [15,30,45,60], cadences [30,45,60],
     and a (size|cadence) → plan map. Only what StayAI actually attached is
     offered; a size or cadence with no plan simply is not in the map. */
  parseLadder(f) {
    const map = {};
    const sizes = new Set();
    const cads = new Set();
    (f.plans || []).forEach((p) => {
      const m = /^Box of (\d+) · every (\d+) days$/.exec(p.name);
      if (!m) return;
      sizes.add(Number(m[1]));
      cads.add(Number(m[2]));
      map[m[1] + '|' + m[2]] = p;
    });
    if (!sizes.size) return null;
    return {
      sizes: [...sizes].sort((a, b) => a - b),
      cadences: [...cads].sort((a, b) => a - b),
      map,
    };
  }

  /* cases to add: a box of 45 is three 15-count cases */
  get qty() {
    if (!this.ladder) return 1;
    return Math.max(1, Math.round(this.ladder.sizes[this.state.size] / this.ladder.sizes[0]));
  }

  /* The plan that is honest for the current selection. Ladder: exact
     size+cadence match from this flavour's own plans. Legacy: the plan whose
     name carries the chosen cadence (the old code always billed the first
     highest-percent plan, silently ignoring the cadence the customer picked). */
  planFor(f) {
    if (this.ladder) {
      const l = this.parseLadder(f);
      if (!l) return null;
      const key = this.ladder.sizes[this.state.size] + '|' + this.cadences[this.state.cadence];
      return l.map[key] || null;
    }
    const plans = f.plans || [];
    if (!plans.length) return null;
    const days = this.cadences[this.state.cadence];
    return plans.find((p) => p.name.indexOf(days + ' day') !== -1) || this.bestPlan;
  }

  t(key, fallback) {
    return this.strings[key] || fallback;
  }

  money(cents) {
    return (
      '$' +
      (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }

  get flavour() {
    return this.data.flavours[this.state.flavour];
  }

  /* The best percentage on offer for this flavour; 0 when StayAI has no plan
     attached, which is also how "subscription unavailable" is detected. */
  get bestPlan() {
    const plans = this.flavour.plans || [];
    if (!plans.length) return null;
    return plans.reduce((a, b) => (b.pct > a.pct ? b : a), plans[0]);
  }

  renderTiles() {
    this.els.tiles.innerHTML = '';
    this.data.flavours.forEach((f, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'o-sub__tile';
      b.setAttribute('role', 'radio');
      b.disabled = !f.available;
      b.innerHTML =
        (f.image
          ? '<img src="' + f.image + '" alt="" width="48" height="48" loading="lazy">'
          : '<span class="o-sub__tile-dot" style="--tile:' + (f.colour || '#1A1815') + '"></span>') +
        '<span>' + f.name + '</span>';
      b.addEventListener('click', () => {
        this.state.flavour = i;
        this.paint();
      });
      this.els.tiles.appendChild(b);
    });
  }

  renderSizes() {
    this.els.sizes.innerHTML = '';
    this.ladder.sizes.forEach((n, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'o-sub__size';
      b.setAttribute('role', 'radio');
      b.addEventListener('click', () => {
        this.state.size = i;
        this.paint();
      });
      this.els.sizes.appendChild(b);
    });
  }

  renderCadence() {
    this.els.cadence.innerHTML = '';
    this.cadences.forEach((days, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'o-sub__pill';
      b.textContent = this.t('everyDays', 'Every {n} days').replace('{n}', days);
      b.addEventListener('click', () => {
        this.state.cadence = i;
        this.paint();
      });
      this.els.cadence.appendChild(b);
    });
  }

  paint() {
    const f = this.flavour;
    const plan = this.planFor(f);
    const total = f.price * this.qty;
    const subPrice = plan ? Math.round(total * (1 - plan.pct / 100)) : total;

    [...this.els.tiles.children].forEach((el, i) =>
      el.classList.toggle('is-on', i === this.state.flavour)
    );
    if (this.els.flavourName) this.els.flavourName.textContent = f.name;

    // No selling plan means one-time is the only truthful option.
    if (!plan && this.state.plan === 'sub') this.state.plan = 'once';

    const subscribing0 = this.state.plan === 'sub' && !!plan;

    /* size tiles reprice with the flavour and the chosen buying mode, the way
       the DPP buybox tiles do: subscription economics when subscribing,
       honest full price per pack when buying once */
    if (this.ladder && this.els.sizes) {
      const l = this.parseLadder(f) || { map: {} };
      const caseSize = this.ladder.sizes[0];
      [...this.els.sizes.children].forEach((el, i) => {
        const n = this.ladder.sizes[i];
        const sizePlan = l.map[n + '|' + this.cadences[this.state.cadence]];
        const pct = subscribing0 && sizePlan ? sizePlan.pct : 0;
        const perPack = Math.round((f.price * (1 - pct / 100)) / caseSize);
        el.innerHTML =
          '<span class="o-sub__size-n">' + n + '<small>' + this.t('packs', 'packs') + '</small></span>' +
          '<span class="o-sub__size-per">' + this.t('perPack', '{p}/pack').replace('{p}', this.money(perPack)) + '</span>' +
          (pct ? '<span class="o-sub__size-save">' + Math.round(pct) + '% ' + this.t('off', 'off') + '</span>' : '');
        el.classList.toggle('is-on', i === this.state.size);
        el.setAttribute('aria-checked', i === this.state.size ? 'true' : 'false');
      });
    }

    const options = [];
    if (plan) {
      options.push({
        key: 'sub',
        name: this.t('subName', 'Subscribe and save'),
        save: Math.round(plan.pct) + '% ' + this.t('off', 'off'),
        price: subPrice,
        was: total,
        sub: this.t('subSub', 'Delivered on repeat. Skip, swap or cancel any time.'),
      });
    }
    options.push({
      key: 'once',
      name: this.t('onceName', 'One-time purchase'),
      price: total,
      sub: this.ladder
        ? this.t('onceSubPacks', '{n} sealed single-serve packs, full price')
            .replace('{n}', this.ladder.sizes[this.state.size])
        : this.t('onceSub', 'A single bag, {n} servings').replace('{n}', this.data.servings),
    });

    this.els.plans.innerHTML = '';
    options.forEach((o) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'o-sub__plan' + (o.key === this.state.plan ? ' is-on' : '');
      b.innerHTML =
        '<span class="o-sub__plan-top">' +
        '<span class="o-sub__plan-name">' + o.name + '</span>' +
        (o.save ? '<span class="o-sub__plan-save">' + o.save + '</span>' : '') +
        '<span class="o-sub__plan-price">' + this.money(o.price) +
        (o.was && o.was !== o.price ? '<span class="o-sub__plan-was">' + this.money(o.was) + '</span>' : '') +
        '</span></span>' +
        '<span class="o-sub__plan-sub">' + o.sub + '</span>';
      b.addEventListener('click', () => {
        this.state.plan = o.key;
        this.paint();
      });
      this.els.plans.appendChild(b);
    });

    const subscribing = this.state.plan === 'sub' && !!plan;
    if (this.els.cadenceStep) this.els.cadenceStep.hidden = !subscribing;
    [...this.els.cadence.children].forEach((el, i) =>
      el.classList.toggle('is-on', i === this.state.cadence)
    );

    const price = subscribing ? subPrice : total;
    this.els.cta.disabled = !f.available;
    this.els.cta.textContent = !f.available
      ? this.t('soldOut', 'Sold out')
      : (subscribing ? this.t('ctaSub', 'Start subscription') : this.t('ctaOnce', 'Add to cart')) +
        ' · ' + this.money(price);

    if (this.els.legal) {
      this.els.legal.textContent = subscribing
        ? this.t('legalSub', 'Billed every {d} days at {p}. Skip, swap or cancel any time from your account.')
            .replace('{d}', this.cadences[this.state.cadence])
            .replace('{p}', this.money(price))
        : this.t('legalOnce', 'One-off order. No subscription, no account needed.');
    }
  }

  wire() {
    this.els.cta.addEventListener('click', () => this.add());

    if (!this.overlay) return;

    const { dialog, nudge } = this.els;

    nudge.addEventListener('click', (e) => {
      // the dismiss affordance sits inside the nudge button
      if (e.target.closest('[data-o-sub-dismiss]')) {
        e.preventDefault();
        e.stopPropagation();
        nudge.hidden = true;
        try {
          sessionStorage.setItem('orion:sub-nudge', 'dismissed');
        } catch (err) {
          /* private mode: dismissing for this page view is enough */
        }
        return;
      }
      dialog.showModal();
    });

    this.querySelectorAll('[data-o-sub-close]').forEach((b) =>
      b.addEventListener('click', () => dialog.close())
    );
    // clicking the backdrop closes, but clicks inside the sheet must not
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem('orion:sub-nudge') === 'dismissed';
    } catch (err) {
      /* ignore */
    }

    // Liquid decided this page should never show it; nothing below may override.
    const blocked = nudge.hasAttribute('data-o-sub-blocked');
    if (dismissed || blocked) nudge.hidden = true;

    // Anything else on the page can open the builder — the cart's subscription
    // banner does exactly this.
    window.orionOpenSubBuilder = () => {
      if (dialog.open) return;
      dialog.showModal();
    };
    /* Anything on the page can open the builder. Three ways in, because most
       of the theme editor only lets a merchant set a LINK, not an attribute:

         • a link to #orion-subscribe   — works from any button block, menu
           item or rich-text link, with no code
         • [data-orion-sub-open-trigger] — for custom HTML
         • the cart's subscription banner

       Registered before the blocked check below on purpose: a button is an
       explicit request, so it must still work on pages where the passive
       prompt is suppressed. */
    const TRIGGERS = [
      'a[href="#orion-subscribe"]',
      'a[href$="#orion-subscribe"]',
      '[data-orion-sub-open-trigger]',
      '[data-orion-sub-banner]',
    ].join(',');

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(TRIGGERS);
      if (!trigger) return;
      e.preventDefault();
      const drawer = document.querySelector('cart-drawer-component dialog[open]');
      if (drawer) drawer.close();
      window.orionOpenSubBuilder();
    });

    /* Landing on /pages/anything#orion-subscribe opens it straight away, so the
       link works as a destination from another page too. */
    if (window.location.hash === '#orion-subscribe') {
      window.setTimeout(() => window.orionOpenSubBuilder(), 300);
    }

    if (blocked) return;

    // Arriving to a prompt already on screen feels like an ad; let the page land first.
    const delay = (parseFloat(nudge.dataset.delay) || 0) * 1000;
    if (!dismissed && delay > 0) {
      nudge.hidden = true;
      window.setTimeout(() => {
        if (!dismissed) this.syncNudge(dismissed);
      }, delay);
    }

    // Horizon updates the cart over AJAX, so the server-rendered flag goes
    // stale the moment someone subscribes. Re-check on every cart change.
    document.addEventListener('orion:cart:added', () => this.syncNudge(dismissed));
    this.syncNudge(dismissed);
  }

  async syncNudge(dismissed) {
    if (dismissed) return;
    if (this.els.nudge.hasAttribute('data-o-sub-blocked')) return;
    try {
      const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
      const hasSub = (cart.items || []).some((i) => i.selling_plan_allocation);
      this.els.nudge.hidden = hasSub;
    } catch (e) {
      /* leave whatever Liquid decided */
    }
  }

  async add() {
    const f = this.flavour;
    const plan = this.planFor(f);
    const subscribing = this.state.plan === 'sub' && !!plan;

    this.els.cta.classList.add('is-loading');
    this.els.cta.disabled = true;
    this.els.error.hidden = true;

    const item = { id: f.variant, quantity: this.qty };
    if (subscribing) item.selling_plan = plan.id;

    try {
      const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.description || 'add failed');

      if (this.els.dialog && this.els.dialog.open) this.els.dialog.close();
      document.dispatchEvent(
        new CustomEvent('orion:cart:added', { detail: { sections: payload.sections } })
      );
    } catch (err) {
      this.els.error.textContent = this.t('addFail', 'Could not add that. Please try again.');
      this.els.error.hidden = false;
    } finally {
      this.els.cta.classList.remove('is-loading');
      this.paint();
    }
  }
}

  customElements.define('orion-sub-builder', OrionSubBuilder);
})();
